import { addDays, differenceInCalendarDays, format } from 'date-fns';
import { createDateSafe } from './dates';
import { ENTITLED_DAYS_PER_PERIOD, computeEndDate, computeEnjoyedDays } from './vacationRules';
import type { VacationModality, VacationSegment } from './vacation.types';

export type FractionedModality = Exclude<VacationModality, '30'>;

export const MODALITY_FIRST_SEGMENT_DAYS: Record<VacationModality, number[]> = {
    '30': [],
    '10+20': [10, 20],
    '15+15': [15],
};

export function isFractionedModality(
    modality: VacationModality | ''
): modality is FractionedModality {
    return modality === '10+20' || modality === '15+15';
}

export function allowedFirstSegmentDays(modality: VacationModality | ''): number[] {
    if (!isFractionedModality(modality)) return [];
    return [...MODALITY_FIRST_SEGMENT_DAYS[modality]];
}

export function allowedFirstEndDates(
    modality: VacationModality | '',
    firstStartDate: string
): string[] {
    if (!createDateSafe(firstStartDate)) return [];

    return allowedFirstSegmentDays(modality)
        .map((days) => computeEndDate(firstStartDate, days))
        .filter(Boolean);
}

export function daysBetweenInclusive(startDate: string, endDate: string): number {
    const start = createDateSafe(startDate);
    const end = createDateSafe(endDate);
    if (!start || !end) return 0;

    const days = differenceInCalendarDays(end, start) + 1;
    return days > 0 ? days : 0;
}

export function resolveFirstSegmentDays(
    modality: VacationModality | '',
    firstStartDate: string,
    firstEndDate: string
): number | null {
    if (!isFractionedModality(modality)) return null;

    const days = daysBetweenInclusive(firstStartDate, firstEndDate);
    return allowedFirstSegmentDays(modality).includes(days) ? days : null;
}

export function resolveSecondSegmentDays(
    modality: VacationModality | '',
    firstSegmentDays: number | null,
    periodDays: number = ENTITLED_DAYS_PER_PERIOD
): number {
    if (!isFractionedModality(modality) || firstSegmentDays === null) return 0;
    return periodDays - firstSegmentDays;
}

export function minSecondStartDate(firstEndDate: string): string {
    const end = createDateSafe(firstEndDate);
    if (!end) return '';
    return format(addDays(end, 1), 'yyyy-MM-dd');
}

export interface VacationSegmentsInput {
    modality: VacationModality | '';
    firstStartDate: string;
    firstEndDate: string;
    secondStartDate: string;
    sellDaysRequested: boolean;
    periodDays?: number;
}

function toSegment(order: number, startDate: string, days: number): VacationSegment {
    return { order, startDate, endDate: computeEndDate(startDate, days), days };
}

export function buildSegments(input: VacationSegmentsInput): VacationSegment[] {
    const { modality, firstStartDate, firstEndDate, secondStartDate, sellDaysRequested, periodDays = ENTITLED_DAYS_PER_PERIOD } = input;

    if (!modality || !createDateSafe(firstStartDate)) return [];

    if (!isFractionedModality(modality)) {
        return [toSegment(1, firstStartDate, computeEnjoyedDays(sellDaysRequested, periodDays))];
    }

    const firstDays = resolveFirstSegmentDays(modality, firstStartDate, firstEndDate);
    if (firstDays === null) return [];

    const segments = [toSegment(1, firstStartDate, firstDays)];

    if (createDateSafe(secondStartDate)) {
        segments.push(toSegment(2, secondStartDate, resolveSecondSegmentDays(modality, firstDays, periodDays)));
    }

    return segments;
}

export function totalEnjoyedDays(segments: VacationSegment[]): number {
    return segments.reduce((sum, segment) => sum + segment.days, 0);
}
